import { emptyHours } from "@/defaults/basic";
import {
  BackendMenu,
  IBackendMenuModuleConfig,
  IBackendMenuModifier,
} from "../../types/backend";
import {
  IMenuModuleConfig,
  Menu,
  IMenuModifier,
  IMenuModifierOption,
} from "../../types/menu";
import { IAgentHours } from "../../types/hours";
import {
  convertHoursToBackend,
  convertHoursToFrontend,
} from "./hours-converter";
import dayjs from "@/lib/dayjs";

const listToPair = (list: string[]) => {
  return Object.fromEntries(list.map((key) => [key, true]));
};

export function convertMenuToFrontend(data: BackendMenu | undefined): Menu {
  if (!data) return [];
  return data.map((category) => ({
    ...category,
    availabilities: category.availabilities
      ? (convertHoursToFrontend(category.availabilities) ?? emptyHours)
      : undefined,
    items: category.items.map((item) => ({
      ...item,
      prepTime: item.prepTime
        ? convertPrepTimeToFrontend(item.prepTime)
        : undefined,
      availabilities: item.availabilities
        ? (convertHoursToFrontend(item.availabilities) ?? emptyHours)
        : undefined,
    })),
  }));
}

export function convertMenuConfigToFrontend(
  data: IBackendMenuModuleConfig,
): IMenuModuleConfig {
  const enabled = true;
  const locked = data.locked;

  const categoryBlacklist = Object.fromEntries(
    (data.categoryBlacklist ?? []).map((key) => [key, true]),
  );
  const categoryNameOverrides = data.categoryNameOverrides ?? {};
  const categoryDescriptionOverrides = data.categoryDescriptionOverrides ?? {};
  const categoryAvailabilityOverrides = Object.fromEntries(
    Object.entries(data.categoryAvailabilityOverrides ?? {}).map(
      ([key, value]) => [key, convertHoursToFrontend(value) ?? emptyHours],
    ),
  );
  const categoryPrepTimeOverrides = Object.fromEntries(
    Object.entries(data.categoryPrepTimeOverrides ?? {}).map(([key, value]) => [
      key,
      convertPrepTimeToFrontend(value),
    ]),
  );

  const itemBlacklist = Object.fromEntries(
    (data.itemBlacklist ?? []).map((key) => [key, true]),
  );
  const itemNameOverrides = data.itemNameOverrides ?? {};
  const itemDescriptionOverrides = data.itemDescriptionOverrides ?? {};
  const itemAvailabilityOverrides = Object.fromEntries(
    Object.entries(data.itemAvailabilityOverrides ?? {}).map(([key, value]) => [
      key,
      convertHoursToFrontend(value) ?? emptyHours,
    ]),
  );
  const defaultOptionsOverrides = Object.fromEntries(
    Object.entries(data.defaultOptionsOverrides ?? {}).map(
      ([itemId, defaultOptionsList]) => [
        itemId,
        listToPair(defaultOptionsList),
      ],
    ),
  );
  const itemPrepTimeOverrides = Object.fromEntries(
    Object.entries(data.itemPrepTimeOverrides ?? {}).map(([key, value]) => [
      key,
      convertPrepTimeToFrontend(value),
    ]),
  );
  const notesMap = data.notesMap ?? {};

  const modifierBlacklist = Object.fromEntries(
    (data.modifierBlacklist ?? []).map((key) => [key, true]),
  );
  const modifierNameOverrides = data.modifierNameOverrides ?? {};

  const forceRequired = Object.fromEntries(
    (data.forceRequired ?? []).map((key) => [key, true]),
  );
  const freeModifierCount = data.freeModifierCount ?? {};
  const freeModifierCountV2 = data.freeModifierCountV2 ?? {};
  const allowStackedOverrides = Object.fromEntries(
    (data.allowStackedOverrides ?? []).map((key) => [key, true]),
  );

  const optionBlacklist = Object.fromEntries(
    (data.optionBlacklist ?? []).map((key) => [key, true]),
  );
  const optionNameOverrides = data.optionNameOverrides ?? {};

  const manualMenu = convertMenuToFrontend(data.manualMenu);
  const replaceMap = data.replaceMap ?? [];

  return {
    enabled,
    locked,
    categoryBlacklist,
    categoryNameOverrides,
    categoryDescriptionOverrides,
    categoryAvailabilityOverrides,
    categoryPrepTimeOverrides,
    itemBlacklist,
    itemNameOverrides,
    itemDescriptionOverrides,
    itemAvailabilityOverrides,
    defaultOptionsOverrides,
    itemPrepTimeOverrides,
    notesMap,
    modifierBlacklist,
    modifierNameOverrides,
    forceRequired,
    freeModifierCount,
    freeModifierCountV2,
    allowStackedOverrides,
    optionBlacklist,
    optionNameOverrides,
    manualMenu,
    replaceMap,
  };
}

export function convertPrepTimeToFrontend(durationStr: string): number {
  try {
    const duration = dayjs.duration(durationStr);
    return duration.asMinutes();
  } catch {
    return 20; // default prep time
  }
}

export function convertPrepTimeToBackend(minutes: number): string {
  return dayjs.duration(minutes, "minutes").toISOString();
}

export function convertMenuToBackend(data: Menu | undefined): BackendMenu {
  if (!data) return [];

  // Helper function to ensure modifiers have options
  const ensureModifierOptions = (
    modifier: IMenuModifier,
  ): IBackendMenuModifier => ({
    ...modifier,
    options: (modifier.options || []).map((option: IMenuModifierOption) => ({
      ...option,
      modifiers: option.modifiers?.map(ensureModifierOptions) || [],
    })),
  });

  return data.map((category) => ({
    ...category,
    availabilities: category.availabilities
      ? convertHoursToBackend(category.availabilities) || undefined
      : undefined,
    items:
      category.items?.map((item) => ({
        ...item,
        prepTime: item.prepTime
          ? convertPrepTimeToBackend(item.prepTime)
          : undefined,
        availabilities: item.availabilities
          ? convertHoursToBackend(item.availabilities) || undefined
          : undefined,
        modifiers: item.modifiers?.map(ensureModifierOptions) || [],
      })) || [],
  }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- grace - before eslint enforced
const removeNullValues = (object: Record<string, any>) => {
  return Object.fromEntries(
    Object.entries(object).filter(
      ([, value]) => value !== null && value !== undefined,
    ),
  );
};

export function convertMenuConfigToBackend(
  data: IMenuModuleConfig,
): IBackendMenuModuleConfig {
  const categoryBlacklist = Object.keys(data.categoryBlacklist ?? {}).filter(
    (key) => data.categoryBlacklist?.[key] === true,
  );
  const categoryNameOverrides = removeNullValues(
    data.categoryNameOverrides ?? {},
  );
  const categoryDescriptionOverrides = removeNullValues(
    data.categoryDescriptionOverrides ?? {},
  );
  const categoryAvailabilityOverrides = Object.fromEntries(
    Object.entries(removeNullValues(data.categoryAvailabilityOverrides ?? {}))
      .map(([key, value]) => [key, convertHoursToBackend(value)])
      .filter(([, value]) => value !== null),
  ) as Record<string, IAgentHours>;
  const categoryPrepTimeOverrides = Object.fromEntries(
    Object.entries(removeNullValues(data.categoryPrepTimeOverrides ?? {})).map(
      ([key, value]) => [key, convertPrepTimeToBackend(value)],
    ),
  );

  const itemBlacklist = Object.keys(data.itemBlacklist ?? {}).filter(
    (key) => data.itemBlacklist?.[key] === true,
  );
  const itemNameOverrides = removeNullValues(data.itemNameOverrides ?? {});
  const itemDescriptionOverrides = removeNullValues(
    data.itemDescriptionOverrides ?? {},
  );
  const itemAvailabilityOverrides = Object.fromEntries(
    Object.entries(removeNullValues(data.itemAvailabilityOverrides ?? {}))
      .map(([key, value]) => [key, convertHoursToBackend(value)])
      .filter(([, value]) => value !== null),
  ) as Record<string, IAgentHours>;
  const defaultOptionsOverrides = Object.fromEntries(
    Object.entries(removeNullValues(data.defaultOptionsOverrides ?? {})).map(
      ([itemId, value]) => [
        itemId,
        Object.keys(value).filter((key) => value[key] === true),
      ],
    ),
  );
  const itemPrepTimeOverrides = Object.fromEntries(
    Object.entries(removeNullValues(data.itemPrepTimeOverrides ?? {})).map(
      ([key, value]) => [key, convertPrepTimeToBackend(value)],
    ),
  );
  const notesMap = removeNullValues(data.notesMap ?? {});

  const modifierBlacklist = Object.keys(data.modifierBlacklist ?? {}).filter(
    (key) => data.modifierBlacklist?.[key] === true,
  );
  const modifierNameOverrides = removeNullValues(
    data.modifierNameOverrides ?? {},
  );

  const forceRequired = Object.keys(data.forceRequired ?? {}).filter(
    (key) => data.forceRequired?.[key] === true,
  );
  const freeModifierCount = removeNullValues(data.freeModifierCount ?? {});
  const freeModifierCountV2 = Object.fromEntries(
    Object.entries(removeNullValues(data.freeModifierCountV2 ?? {})).map(
      ([itemId, modifierMap]) => [itemId, removeNullValues(modifierMap)],
    ),
  );
  const allowStackedOverrides = Object.keys(
    data.allowStackedOverrides ?? {},
  ).filter((key) => data.allowStackedOverrides?.[key] === true);

  const optionBlacklist = Object.keys(data.optionBlacklist ?? {}).filter(
    (key) => data.optionBlacklist?.[key] === true,
  );
  const optionNameOverrides = removeNullValues(data.optionNameOverrides ?? {});

  const manualMenu = convertMenuToBackend(data.manualMenu);
  const replaceMap = data.replaceMap ?? [];

  return {
    enabled: data.enabled,
    locked: data.locked,
    categoryBlacklist,
    categoryNameOverrides,
    categoryDescriptionOverrides,
    categoryAvailabilityOverrides,
    categoryPrepTimeOverrides,
    itemBlacklist,
    itemNameOverrides,
    itemDescriptionOverrides,
    itemAvailabilityOverrides,
    defaultOptionsOverrides,
    itemPrepTimeOverrides,
    notesMap,
    modifierBlacklist,
    modifierNameOverrides,
    forceRequired,
    freeModifierCount,
    freeModifierCountV2,
    allowStackedOverrides,
    optionBlacklist,
    optionNameOverrides,
    manualMenu,
    replaceMap,
  };
}
